import { isProfileField } from '../profileContract/profileFieldVocabulary';
import {
  PROFILE_PLACEMENT_MODES,
  normalizeProfilePlacementChapterId,
  normalizeProfilePlacementMode,
} from '../profileContract/profilePlacementMetadata';

export const KNOWLEDGE_ITEMS_COLLECTION = 'knowledgeItems';

export const KNOWLEDGE_ITEM_TYPES = Object.freeze({
  PROFILE: 'profile',
  RISK: 'risk',
  TRIGGER: 'trigger',
  STRATEGY: 'strategy',
  SAFETY: 'safety',
  FEEDING: 'feeding',
  SLEEP: 'sleep',
  MEDICATION: 'medication',
  PREFERENCE: 'preference',
  GOAL: 'goal',
  INSTRUCTION: 'instruction',
  SUPPORT: 'support',
  CONTACT: 'contact',
  CONDITION: 'condition',
  ROUTINE: 'routine',
  BASELINE: 'baseline',
  NOTE: 'note',
  OTHER: 'other',
});

const VALID_KNOWLEDGE_TYPES = new Set(Object.values(KNOWLEDGE_ITEM_TYPES));
const VALID_CONFIDENCE_VALUES = new Set(['high', 'medium', 'low']);
const normalizeText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

export const normalizeKnowledgeItemType = (value = '') => {
  const normalized = normalizeText(value).toLowerCase().replace(/[\s-]+/g, '_');
  return VALID_KNOWLEDGE_TYPES.has(normalized)
    ? normalized
    : KNOWLEDGE_ITEM_TYPES.OTHER;
};

const normalizeKnownConfidence = (value = '') => {
  const normalized = normalizeText(value).toLowerCase();
  return VALID_CONFIDENCE_VALUES.has(normalized) ? normalized : '';
};

const normalizeSourceProvenance = (value = {}) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const origin = normalizeText(value.origin).toLowerCase();
  const label = normalizeText(value.label);
  const sourceKind = normalizeText(value.sourceKind).toLowerCase();
  const editableByCaregiver = typeof value.editableByCaregiver === 'boolean'
    ? value.editableByCaregiver
    : null;

  return Object.fromEntries(Object.entries({
    ...(origin ? { origin } : {}),
    ...(label ? { label } : {}),
    ...(sourceKind ? { sourceKind } : {}),
    ...(editableByCaregiver === null ? {} : { editableByCaregiver }),
  }).filter(([, itemValue]) => itemValue !== '' && itemValue !== null && itemValue !== undefined));
};

export const buildKnowledgeItemPayload = ({
  childId = '',
  captureId = '',
  title = '',
  summary = '',
  knowledgeType = KNOWLEDGE_ITEM_TYPES.OTHER,
  profileField = '',
  profilePlacementMode = PROFILE_PLACEMENT_MODES.AUTOMATIC,
  profileChapterId = '',
  customFactType = '',
  sourceText = '',
  sourceBlockId = '',
  sourceSentenceIndex = null,
  bucket = '',
  sourceRefs = [],
  extractionVersion = '',
  editedByUser = false,
  confidence = '',
  sourceProvenance = {},
  capturedAt,
  capturedBy = {},
  createdAt,
  updatedAt,
} = {}) => {
  const payload = {
    childId: normalizeText(childId),
    captureId: normalizeText(captureId),
    title: normalizeText(title) || 'Care knowledge',
    summary: normalizeText(summary || sourceText),
    knowledgeType: normalizeKnowledgeItemType(knowledgeType),
    factType: normalizeKnowledgeItemType(knowledgeType),
    bucket: normalizeKnowledgeItemType(bucket || knowledgeType),
    sourceText: normalizeText(sourceText),
    extractionVersion: normalizeText(extractionVersion),
    editedByUser: Boolean(editedByUser),
    capturedAt,
    capturedBy: capturedBy && typeof capturedBy === 'object' && !Array.isArray(capturedBy)
      ? capturedBy
      : {},
    createdAt,
    updatedAt,
    schemaVersion: 2,
    factStatus: 'current',
  };

  const cleanSourceProvenance = normalizeSourceProvenance(sourceProvenance);
  if (Object.keys(cleanSourceProvenance).length) {
    payload.sourceProvenance = cleanSourceProvenance;
  }

  const cleanCustomFactType = normalizeText(customFactType).toLowerCase().replace(/[\s-]+/g, '_').slice(0, 80);
  if (payload.knowledgeType === KNOWLEDGE_ITEM_TYPES.OTHER && cleanCustomFactType) {
    payload.customFactType = cleanCustomFactType;
  }

  const cleanProfileField = normalizeText(profileField);
  if (isProfileField(cleanProfileField)) {
    payload.profileField = cleanProfileField;
  }

  const cleanPlacementMode = normalizeProfilePlacementMode(profilePlacementMode);
  const cleanProfileChapterId = normalizeProfilePlacementChapterId(profileChapterId);
  if (cleanProfileChapterId) {
    payload.profilePlacementMode = cleanPlacementMode;
    payload.profileChapterId = cleanProfileChapterId;
  }

  const cleanSourceBlockId = normalizeText(sourceBlockId);
  if (cleanSourceBlockId) {
    payload.sourceBlockId = cleanSourceBlockId;
  }

  if (Number.isInteger(sourceSentenceIndex)) {
    payload.sourceSentenceIndex = sourceSentenceIndex;
  }

  const cleanSourceRefs = Array.isArray(sourceRefs)
    ? sourceRefs
      .map((sourceRef) => {
        if (!sourceRef || typeof sourceRef !== 'object' || Array.isArray(sourceRef)) return null;
        const cleanRef = {
          sourceId: normalizeText(sourceRef.sourceId),
          authorId: normalizeText(sourceRef.authorId),
          authorName: normalizeText(sourceRef.authorName),
          capturedAt: sourceRef.capturedAt || null,
          sourceVersion: normalizeText(sourceRef.sourceVersion),
          timelineVersion: normalizeText(sourceRef.timelineVersion),
          supportStatus: normalizeText(sourceRef.supportStatus),
          confirmationReason: normalizeText(sourceRef.confirmationReason),
          sourceTitle: normalizeText(sourceRef.sourceTitle),
          sourceType: normalizeText(sourceRef.sourceType),
          sourceContext: normalizeText(sourceRef.sourceContext),
          sourceCollection: normalizeText(sourceRef.sourceCollection),
          timelineEntryId: normalizeText(sourceRef.timelineEntryId),
          timelineDate: normalizeText(sourceRef.timelineDate),
          timelineDateLabel: normalizeText(sourceRef.timelineDateLabel),
          sourceText: normalizeText(sourceRef.sourceText),
          confidence: normalizeText(sourceRef.confidence).toLowerCase(),
          confirmedAt: sourceRef.confirmedAt || null,
          confirmedBy: sourceRef.confirmedBy || null,
        };
        return Object.fromEntries(Object.entries(cleanRef).filter(([, value]) => (
          value !== '' && value !== null && value !== undefined
        )));
      })
      .filter((sourceRef) => sourceRef && Object.keys(sourceRef).length)
      .slice(0, 10)
    : [];

  if (cleanSourceRefs.length) {
    payload.sourceRefs = cleanSourceRefs;
  }

  const cleanConfidence = normalizeKnownConfidence(confidence)
    || cleanSourceRefs.map((sourceRef) => normalizeKnownConfidence(sourceRef.confidence)).find(Boolean)
    || normalizeKnownConfidence(payload.capturedBy.confidence);
  if (cleanConfidence) {
    payload.confidence = cleanConfidence;
  }

  return payload;
};
